// Talking to Artemis on web (WEB-7116, API-3690): what to read aloud for a reply. Dictation
// and speaking themselves are the browser's (useDictation, useSpeaker); this decides the words.

import { answerToText } from './context'

const MAX_WORDS = 60

// Text fit to speak: no citation markers, links, markdown or runs of whitespace.
export const cleanForSpeech = text => String(text || '')
  .replace(/\[\d+\]/g, '')
  .replace(/https?:\/\/\S+/g, '')
  .replace(/[*_#`>]+/g, '')
  .replace(/^\s*-\s+/gm, '')
  .replace(/\s+([.,;:!?])/g, '$1')
  .replace(/\s+/g, ' ')
  .trim()

// At most MAX_WORDS words, ending on a sentence where it can.
const clip = text => {
  const words = text.split(' ')
  if (words.length <= MAX_WORDS) return text
  const cut = words.slice(0, MAX_WORDS).join(' ')
  const end = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('? '), cut.lastIndexOf('! '))
  return end > 20 ? cut.slice(0, end + 1) : `${cut}…`
}

// The first two sentences of an answer, for when the server sends no speech event.
export const fallbackSpeech = html => {
  const text = cleanForSpeech(answerToText(html))
  if (!text) return null
  const sentences = text.match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g) || [text]
  return clip(sentences.slice(0, 2).join(' ').replace(/\s+/g, ' ').trim())
}

// What to say for a reply, or null (yet). Only replies asked with read aloud on are spoken:
// the server's speech event as soon as it arrives, otherwise the start of the answer once it's
// done. Failed and stopped replies aren't spoken.
export const speechFor = message => {
  if (!message || !message.readAloud) return null
  if (message.status !== 'streaming' && message.status !== 'complete') return null
  if (message.speech) return clip(cleanForSpeech(message.speech)) || null
  if (message.status === 'complete' && message.answer) return fallbackSpeech(message.answer)
  return null
}

// Reading the whole answer (WEB-7123): sentence by sentence as it streams, so speaking starts
// early. 'full' reads the answer; 'summary' reads the server's speech event, as above.
export const READ_MODES = ['full', 'summary']
// Polly takes at most 3,000 characters a call (API-3703).
const MAX_CLIP_CHARS = 3000

// Words a full stop after doesn't end a sentence: "Mt. Zirkel", "No. 4", "approx. 3 miles".
const ABBREVIATIONS = new Set(['mt', 'st', 'ft', 'dr', 'mr', 'mrs', 'ms', 'no', 'vs', 'approx', 'est', 'jan', 'feb', 'mar', 'apr',
  'jun', 'jul', 'aug', 'sep', 'sept', 'oct', 'nov', 'dec', 'hwy', 'rd', 'co', 'etc', 'e.g', 'i.e', 'u.s', 'a.m', 'p.m'])

// A line's sentences, and what's left after the last one ends. A sentence ends at . ! or ?
// (and any closing quote or bracket) followed by a space, or at the end of a finished line.
const splitLine = (line, finished) => {
  const sentences = []
  const boundary = /[.!?]+["'”’)\]]*(\s+|$)/g
  let start = 0, match
  while ((match = boundary.exec(line))) {
    const end = match.index + match[0].length
    if (!match[1] && !finished) break
    if (match[0].startsWith('.')) {
      const words = line.slice(start, match.index).split(/\s+/)
      const word = words.pop().toLowerCase()
      // A unit after a number ends a sentence when one starts next: "at 8,870 ft. It's".
      const unitEnds = /\d$/.test(words.pop() || '') && /^[A-Z“"']/.test(line.slice(end))
      // An abbreviation or an initial ("J. Smith"); a line that ends there still ends.
      if ((ABBREVIATIONS.has(word) || /^[a-z]$/.test(word)) && !unitEnds && end < line.length) continue
    }
    const sentence = line.slice(start, end).trim()
    if (sentence) sentences.push(sentence)
    start = end
    if (!match[0].length) boundary.lastIndex++
  }
  return { sentences, rest: line.slice(start).trim() }
}

// The answer's sentences, ready to speak. While it streams, the sentence still being written is
// left out; once complete is true, everything is in. List items are read as sentences.
export const answerSentences = (html, complete) => {
  if (!html) return []
  const lines = answerToText(html).split(/\n+/)
  const out = []
  lines.forEach((raw, i) => {
    const last = i === lines.length - 1
    const finished = !last || complete
    const line = cleanForSpeech(raw.replace(/^\s*(-|\d+[.)])\s+/, ''))
    if (!line) return
    const { sentences, rest } = splitLine(line, finished)
    out.push(...sentences)
    // A finished line without an ending ("First season: September 2") is a sentence too.
    if (rest && finished) out.push(/[.!?]$/.test(rest) ? rest : `${rest}.`)
  })
  return out.filter(s => /[a-z0-9]/i.test(s)).map(s => s.slice(0, MAX_CLIP_CHARS))
}

// A step worth calling out while the reply runs: tools, searches and skills, never reasoning.
const CALLOUT_KINDS = new Set(['tool', 'search', 'skill'])
export const progressLine = step => {
  if (!step || !CALLOUT_KINDS.has(step.kind) || step.status === 'failed') return null
  return cleanForSpeech(step.title) || null
}

// What's new to say for a reply, given what's been said (plan: { steps: [stepIds], sentences,
// summary }). Returns { progress, answer, plan }. Progress is only for steps before the answer
// starts; steps seen later are marked said without being spoken.
export const nextSpeech = (message, mode, plan = { steps: [], sentences: 0, summary: false }) => {
  const none = { progress: [], answer: [], plan }
  if (!message || !message.readAloud) return none
  if (message.status !== 'streaming' && message.status !== 'complete') return none
  const answering = !!message.answer || !!message.speech && mode === 'summary'
  const progress = []
  const steps = [...plan.steps]
  ;(message.steps || []).forEach(step => {
    if (!step.stepId || steps.includes(step.stepId)) return
    const line = progressLine(step)
    if (!line) return
    steps.push(step.stepId)
    if (!answering) progress.push(line)
  })
  let answer = []
  let { sentences, summary } = plan
  if (mode === 'summary') {
    const said = summary ? null : speechFor(message)
    if (said) { answer = [said]; summary = true }
  } else {
    const ready = answerSentences(message.answer, message.status === 'complete')
    answer = ready.slice(sentences)
    sentences = Math.max(sentences, ready.length)
  }
  return { progress, answer, plan: { steps, sentences, summary } }
}

// Browser voices, best first, in the page's language: Natural/Neural (Edge), then Premium/
// Enhanced (Safari), then Google (Chrome), then the rest. Safari's novelty voices are left out.
const NOVELTY = /^(albert|bad news|bahh|bells|boing|bubbles|cellos|good news|jester|organ|superstar|trinoids|whisper|wobble|zarvox|junior|ralph|fred|kathy|deranged|hysterical|pipe organ)\b/i
const voiceScore = (voice, lang) => {
  const label = `${voice.name} ${voice.voiceURI || ''}`
  let score = /natural|neural/i.test(label) ? 40 : /premium|enhanced/i.test(label) ? 30 : /^google/i.test(voice.name) ? 20 : 10
  if ((voice.lang || '').toLowerCase().replace('_', '-') === lang.toLowerCase()) score += 2
  if (voice.default) score += 1
  return score
}
export const rankVoices = (voices, lang = 'en-US') => {
  const family = lang.split('-')[0].toLowerCase()
  return (voices || [])
    .filter(v => (v.lang || '').toLowerCase().startsWith(family) && !NOVELTY.test(v.name))
    .map((v, i) => ({ v, i, score: voiceScore(v, lang) }))
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map(x => x.v)
}
